import { Box, Button, Grid, HStack, NativeSelect, Spinner, Text, VStack } from '@chakra-ui/react'
import { useEffect, useState } from 'react'
import AddAddressForm, { AnalysisChecklist } from '../../components/portfolio/AddAddressForm'
import ChimnieAddressForm from '../components/ChimnieAddressForm'
import PilotIntro from '../components/PilotIntro'
import PilotResults from '../components/PilotResults'
import pilotHttp from '../pilotHttp'
import { usePilotSession } from '../PilotSessionContext'
import { LeadPayload, PilotAssessmentResult, PilotStep } from '../pilotTypes'

const PILOT_RESULT_VERSION = 6
const resultKey = (council: string) => `pilot_result_v${PILOT_RESULT_VERSION}_${council}`
const contextKey = (council: string) => `pilot_result_context_v${PILOT_RESULT_VERSION}_${council}`
const formKey = (council: string) => `pilot_form_v1_${council}`
// Deliberately unversioned and not cleared on "start over": once someone has
// left their contact details, we never ask them again on this device.
const unlockKey = (council: string) => `pilot_prices_unlocked_v1_${council}`

const BANK_OPTIONS = [
  'Lloyds Bank',
  'NatWest',
  'Nationwide',
  'HSBC UK',
  'Bank of Scotland / Halifax',
  'Barclays',
  'Santander',
  'Royal Bank of Scotland',
  'Virgin Money',
  'TSB',
  'Other bank',
]

interface PilotAssessmentContext {
  address: string
  occupancyType?: string
  incomeBelowThreshold?: string
  mortgageOrBankProvider?: string
  administrativeArea?: string
}

const normalizeContextValue = (value: unknown): string | undefined => {
  const normalized = typeof value === 'string' ? value.trim() : String(value ?? '').trim()
  return normalized || undefined
}

const assessmentContextFromPayload = (payload: any): PilotAssessmentContext => ({
  address: normalizeContextValue(payload.address) ?? '',
  occupancyType: normalizeContextValue(payload.occupancyType ?? payload.occupancy_type),
  incomeBelowThreshold: normalizeContextValue(payload.incomeBelowThreshold ?? payload.income_below_threshold),
  mortgageOrBankProvider: normalizeContextValue(payload.mortgageOrBankProvider ?? payload.mortgage_or_bank_provider),
  administrativeArea: normalizeContextValue(payload.administrativeArea ?? payload.administrative_area),
})

const sameAssessmentContext = (
  previous: PilotAssessmentContext | null,
  next: PilotAssessmentContext,
): boolean => {
  if (!previous) return false
  return previous.address === next.address
    && previous.occupancyType === next.occupancyType
    && previous.incomeBelowThreshold === next.incomeBelowThreshold
    && previous.mortgageOrBankProvider === next.mortgageOrBankProvider
    && previous.administrativeArea === next.administrativeArea
}

const loadStoredAssessmentContext = (council: string): PilotAssessmentContext | null => {
  try {
    const raw = localStorage.getItem(contextKey(council))
    return raw ? (JSON.parse(raw) as PilotAssessmentContext) : null
  } catch {
    return null
  }
}

const PilotPage: React.FC = () => {
  const { council, config, configLoading } = usePilotSession()

  const [assessmentResult, setAssessmentResult] = useState<PilotAssessmentResult | null>(() => {
    try {
      const raw = localStorage.getItem(resultKey(council))
      return raw ? (JSON.parse(raw) as PilotAssessmentResult) : null
    } catch { return null }
  })

  const [step, setStep] = useState<PilotStep>(() => {
    try {
      return localStorage.getItem(resultKey(council)) ? 'results' : 'intro'
    } catch { return 'intro' }
  })

  const [lastAssessmentContext, setLastAssessmentContext] = useState<PilotAssessmentContext | null>(() => (
    loadStoredAssessmentContext(council)
  ))

  const [isAssessing, setIsAssessing] = useState(false)
  const [isSubmittingLead, setIsSubmittingLead] = useState(false)
  const [assessError, setAssessError] = useState<string | null>(null)
  const [assessErrorTitle, setAssessErrorTitle] = useState<string | null>(null)
  const [assessErrorLinkText, setAssessErrorLinkText] = useState<string | null>(null)
  const [assessErrorLinkUrl, setAssessErrorLinkUrl] = useState<string | null>(null)
  const [leadError, setLeadError] = useState<string | null>(null)

  const [pricesUnlocked, setPricesUnlocked] = useState<boolean>(() => {
    try {
      return localStorage.getItem(unlockKey(council)) === 'true'
    } catch { return false }
  })

  const [pendingAddressDetails, setPendingAddressDetails] = useState<any>(() => {
    try {
      const raw = localStorage.getItem(formKey(council))
      return raw ? JSON.parse(raw).pendingAddressDetails ?? null : null
    } catch { return null }
  })
  const [occupancyType, setOccupancyType] = useState<'owner_occupier' | 'tenant' | 'landlord' | ''>(() => {
    try {
      const raw = localStorage.getItem(formKey(council))
      return raw ? JSON.parse(raw).occupancyType ?? '' : ''
    } catch { return '' }
  })
  const [incomeBelowThreshold, setIncomeBelowThreshold] = useState<'yes' | 'no' | 'not_sure' | ''>(() => {
    try {
      const raw = localStorage.getItem(formKey(council))
      return raw ? JSON.parse(raw).incomeBelowThreshold ?? '' : ''
    } catch { return '' }
  })
  const [mortgageOrBankProvider, setMortgageOrBankProvider] = useState(() => {
    try {
      const raw = localStorage.getItem(formKey(council))
      return raw ? JSON.parse(raw).mortgageOrBankProvider ?? '' : ''
    } catch { return '' }
  })
  const [addressFormError, setAddressFormError] = useState<string | null>(null)
  const [freshAddressSelected, setFreshAddressSelected] = useState(false)

  useEffect(() => {
    try {
      if (pendingAddressDetails || occupancyType || incomeBelowThreshold || mortgageOrBankProvider) {
        localStorage.setItem(formKey(council), JSON.stringify({
          pendingAddressDetails,
          occupancyType,
          incomeBelowThreshold,
          mortgageOrBankProvider,
        }))
      } else {
        localStorage.removeItem(formKey(council))
      }
    } catch {}
  }, [pendingAddressDetails, occupancyType, incomeBelowThreshold, mortgageOrBankProvider, council])

  if (configLoading) {
    return (
      <Box minH="100vh" display="flex" alignItems="center" justifyContent="center">
        <Spinner size="xl" />
      </Box>
    )
  }

  if (!config) {
    return (
      <Box minH="100vh" display="flex" alignItems="center" justifyContent="center" p={8}>
        <Text color="gray.500">This pilot is not available. Please check the URL.</Text>
      </Box>
    )
  }

  const clearAssessError = () => {
    setAssessError(null)
    setAssessErrorTitle(null)
    setAssessErrorLinkText(null)
    setAssessErrorLinkUrl(null)
  }

  // Shared by both address providers: a freshly picked address resets the
  // follow-up questions so stale answers never ride along with a new home.
  const handleAddressSelected = (details: any) => {
    setPendingAddressDetails(details)
    setFreshAddressSelected(true)
    setOccupancyType('')
    setIncomeBelowThreshold('')
    setMortgageOrBankProvider('')
    setAddressFormError(null)
  }

  const handleAddressSubmit = async (payload: any) => {
    clearAssessError()

    const currentAssessmentContext = assessmentContextFromPayload(payload)
    if (assessmentResult && sameAssessmentContext(lastAssessmentContext, currentAssessmentContext)) {
      setStep('results')
      return
    }

    setIsAssessing(true)
    setStep('loading')
    try {
      const res = await pilotHttp.post(`/api/pilot/${council}/assess/`, payload)
      setAssessmentResult(res.data)
      setLastAssessmentContext(currentAssessmentContext)
      try { localStorage.setItem(resultKey(council), JSON.stringify(res.data)) } catch {}
      try { localStorage.setItem(contextKey(council), JSON.stringify(currentAssessmentContext)) } catch {}
      setStep('results')
    } catch (err: any) {
      const errorCode = err?.response?.data?.error_code
      const errorMsg = err?.response?.data?.error
      const errorTitle = err?.response?.data?.error_title
      const errorLinkText = err?.response?.data?.error_link_text
      const errorLinkUrl = err?.response?.data?.error_link_url

      if (errorCode === 'POSTCODE_NOT_ALLOWED') {
        setAssessError(errorMsg ?? 'This address is outside the covered area.')
        setAssessErrorTitle(errorTitle ?? null)
        setAssessErrorLinkText(errorLinkText ?? null)
        setAssessErrorLinkUrl(errorLinkUrl ?? null)
      } else if (errorCode === 'RATE_LIMITED') {
        setAssessError('You have submitted too many requests. Please try again later.')
      } else {
        setAssessError(errorMsg ?? 'Something went wrong. Please try again.')
      }
      setStep('address')
    } finally {
      setIsAssessing(false)
    }
  }

  // Records the session as having opened the lead form so the dashboard funnel
  // can show users who opened it but never submitted. Fire-and-forget.
  const handleGetPricesStart = () => {
    pilotHttp.post(`/api/pilot/${council}/book-started/`, {}).catch(() => {})
  }

  const handleLeadSubmit = async (payload: LeadPayload) => {
    setLeadError(null)
    setIsSubmittingLead(true)
    try {
      await pilotHttp.post(`/api/pilot/${council}/book/`, payload)
      setPricesUnlocked(true)
      try { localStorage.setItem(unlockKey(council), 'true') } catch {}
    } catch (err: any) {
      const data = err?.response?.data
      // DRF validation errors come back as {field: [messages]} or {non_field_errors: [...]}
      const validationMessage = Array.isArray(data?.nonFieldErrors) ? data.nonFieldErrors[0] : undefined
      setLeadError(data?.error ?? validationMessage ?? 'Failed to submit your details. Please try again.')
    } finally {
      setIsSubmittingLead(false)
    }
  }

  const handleStartOver = () => {
    try { localStorage.removeItem(resultKey(council)) } catch {}
    try { localStorage.removeItem(contextKey(council)) } catch {}
    try { localStorage.removeItem(formKey(council)) } catch {}
    setStep('intro')
    setAssessmentResult(null)
    setLastAssessmentContext(null)
    setAssessError(null)
    setAssessErrorTitle(null)
    setAssessErrorLinkText(null)
    setAssessErrorLinkUrl(null)
    setLeadError(null)
    setPendingAddressDetails(null)
    setOccupancyType('')
    setIncomeBelowThreshold('')
    setMortgageOrBankProvider('')
    setFreshAddressSelected(false)
    setAddressFormError(null)
  }

  const handlePilotNext = async () => {
    if (!pendingAddressDetails) {
      setAddressFormError('Please select an address from the dropdown.')
      return
    }
    if (!occupancyType) {
      setAddressFormError('Please tell us your relationship to this property.')
      return
    }
    if (!incomeBelowThreshold) {
      setAddressFormError('Please answer the household income question.')
      return
    }
    if (!mortgageOrBankProvider) {
      setAddressFormError('Please choose your bank.')
      return
    }
    setAddressFormError(null)
    await handleAddressSubmit({
      address: pendingAddressDetails.fullAddress,
      postcode: pendingAddressDetails.postcode,
      streetNumber: pendingAddressDetails.streetNumber,
      streetName: pendingAddressDetails.route,
      locality: pendingAddressDetails.locality,
      city: pendingAddressDetails.city,
      administrativeArea: pendingAddressDetails.administrativeArea,
      country: pendingAddressDetails.country,
      sublocality: pendingAddressDetails.sublocality,
      ward: pendingAddressDetails.ward,
      postalTown: pendingAddressDetails.postalTown,
      adminArea2: pendingAddressDetails.adminArea2,
      adminArea3: pendingAddressDetails.adminArea3,
      shortAddress: pendingAddressDetails.shortAddress,
      occupancyType,
      incomeBelowThreshold: incomeBelowThreshold || undefined,
      mortgageOrBankProvider: mortgageOrBankProvider.trim() || undefined,
    })
  }

  return (
    <Box minH="100vh" bg="gray.50">

      {/* Step: Intro */}
      {step === 'intro' && (
        <PilotIntro config={config} onStart={() => setStep('address')} />
      )}

      {/* Step: Address entry */}
      {(step === 'address' || step === 'loading') && (
        <Box maxW="620px" mx="auto" py={{ base: 6, sm: 10 }} px={4}>
          <VStack gap={6} align="stretch">
            <Box>
              <Text fontSize="xl" fontWeight="semibold" color="gray.800" mb={1}>
                Enter your home address
              </Text>
              <Text fontSize="sm" color="gray.500">
                We'll use this to look up your property's EPC data and run your assessment.
              </Text>
            </Box>
            {/* When returning from results with a stored address, show a compact banner
                instead of requiring the user to re-select from the dropdown */}
            {pendingAddressDetails && !freshAddressSelected && (
              <Box
                bg="white"
                border="1px solid"
                borderColor="gray.200"
                borderRadius="lg"
                px={4}
                py={3}
                display="flex"
                justifyContent="space-between"
                gap={3}
                flexDirection={{ base: 'column', sm: 'row' }}
                alignItems={{ base: 'stretch', sm: 'center' }}
              >
                <Box minW={0}>
                  <Text fontSize="sm" fontWeight="semibold" color="gray.800" overflowWrap="anywhere">
                    {pendingAddressDetails.shortAddress}, {pendingAddressDetails.postcode}
                  </Text>
                  {pendingAddressDetails.city && (
                    <Text fontSize="xs" color="gray.400">{pendingAddressDetails.city}</Text>
                  )}
                </Box>
                <Button
                  variant="ghost"
                  size="sm"
                  color="gray.500"
                  flexShrink={0}
                  alignSelf={{ base: 'flex-start', sm: 'auto' }}
                  onClick={() => {
                    setPendingAddressDetails(null)
                    setOccupancyType('')
                    setIncomeBelowThreshold('')
                    setMortgageOrBankProvider('')
                    setFreshAddressSelected(false)
                    setAddressFormError(null)
                  }}
                >
                  Change
                </Button>
              </Box>
            )}

            {(!pendingAddressDetails || freshAddressSelected) && (
              config.addressProvider === 'chimnie' ? (
                <ChimnieAddressForm
                  council={council}
                  onAddressSelected={handleAddressSelected}
                  onClearApiError={clearAssessError}
                  brandColor={config.brandPrimaryColor}
                />
              ) : (
                <AddAddressForm
                  onSubmit={handleAddressSubmit}
                  hideSubmitButton
                  showAnalysisChecklist={false}
                  onAddressSelected={handleAddressSelected}
                  isSubmitting={isAssessing}
                  onClearApiError={clearAssessError}
                  brandColor={config.brandPrimaryColor}
                />
              )
            )}

            {pendingAddressDetails && (
              <VStack gap={5} align="stretch">
                {/* Q1: Occupancy type */}
                <Box>
                  <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={3}>
                    What is your relationship to this property?
                  </Text>
                  <Grid templateColumns={{ base: '1fr', sm: 'repeat(3, 1fr)' }} gap={2}>
                    {([
                      { value: 'owner_occupier', label: 'Owner-occupier' },
                      { value: 'tenant', label: 'Tenant' },
                      { value: 'landlord', label: 'Landlord' },
                    ] as const).map(opt => (
                      <Box
                        key={opt.value}
                        as="button"
                        textAlign="center"
                        flex={1}
                        px={3}
                        py={2.5}
                        borderRadius="lg"
                        border="2px solid"
                        borderColor={occupancyType === opt.value ? 'green.500' : 'gray.200'}
                        bg={occupancyType === opt.value ? 'green.50' : 'white'}
                        cursor="pointer"
                        _hover={{ borderColor: occupancyType === opt.value ? 'green.500' : 'gray.300' }}
                        onClick={() => setOccupancyType(opt.value)}
                        transition="all 0.15s"
                      >
                        <Text fontSize="sm" fontWeight="semibold" color="gray.800">{opt.label}</Text>
                      </Box>
                    ))}
                  </Grid>
                </Box>

                {/* Q2: Household income threshold */}
                <Box>
                  <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={3}>
                    {occupancyType === 'landlord'
                      ? "Is your tenant(s)' total annual household income £36,000 or less before tax?"
                      : 'Is your total annual household income £36,000 or less before tax?'}
                  </Text>
                  <HStack gap={2}>
                    {([
                      { value: 'yes', label: 'Yes' },
                      { value: 'no', label: 'No' },
                      { value: 'not_sure', label: 'Not sure' },
                    ] as const).map(opt => (
                      <Box
                        key={opt.value}
                        as="button"
                        textAlign="center"
                        flex={1}
                        px={3}
                        py={2.5}
                        borderRadius="lg"
                        border="2px solid"
                        borderColor={incomeBelowThreshold === opt.value ? 'green.500' : 'gray.200'}
                        bg={incomeBelowThreshold === opt.value ? 'green.50' : 'white'}
                        cursor="pointer"
                        _hover={{ borderColor: incomeBelowThreshold === opt.value ? 'green.500' : 'gray.300' }}
                        onClick={() => setIncomeBelowThreshold(opt.value)}
                        transition="all 0.15s"
                      >
                        <Text fontSize="sm" fontWeight="semibold" color="gray.800">{opt.label}</Text>
                      </Box>
                    ))}
                  </HStack>
                </Box>

                {/* Q3: Mortgage or everyday bank */}
                <Box>
                  <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={1}>
                    Which bank do you use for your mortgage or everyday banking?
                  </Text>
                  <Text fontSize="sm" color="gray.600" mb={3}>
                    We ask this so we can check whether your bank offers support for making your home greener,
                    such as extra borrowing or cashback. Not all banks offer this.
                  </Text>
                  <NativeSelect.Root size="lg">
                    <NativeSelect.Field
                      value={mortgageOrBankProvider}
                      onChange={(event) => setMortgageOrBankProvider(event.target.value)}
                      bg="white"
                      borderColor="gray.200"
                      disabled={isAssessing}
                    >
                      <option value="">Select a bank</option>
                      {BANK_OPTIONS.map(bank => (
                        <option key={bank} value={bank}>{bank}</option>
                      ))}
                    </NativeSelect.Field>
                    <NativeSelect.Indicator />
                  </NativeSelect.Root>
                </Box>

                {(assessError || addressFormError) && (
                  <VStack gap={2} align="stretch">
                    {assessError && (
                      <Box
                        bg="red.50"
                        border="1px solid"
                        borderColor="red.200"
                        color="red.800"
                        px={4}
                        py={3}
                        borderRadius="md"
                        fontSize="sm"
                      >
                        <Text fontWeight="bold" mb={1}>
                          ⚠️ {assessErrorTitle || "We couldn't check this address"}
                        </Text>
                        <Text>
                          {assessError}
                          {assessErrorLinkUrl && assessErrorLinkText && (
                            <>
                              {' '}
                              <a
                                href={assessErrorLinkUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: '#2B6CB0', textDecoration: 'underline' }}
                              >
                                {assessErrorLinkText}
                              </a>
                            </>
                          )}
                        </Text>
                      </Box>
                    )}
                    {addressFormError && (
                      <Text color="red.500" fontSize="sm">{addressFormError}</Text>
                    )}
                  </VStack>
                )}

                <Button
                  bg="brand.500"
                  color="white"
                  _hover={{ opacity: 0.9 }}
                  size="lg"
                  onClick={handlePilotNext}
                  disabled={isAssessing}
                  loading={isAssessing}
                  loadingText="Assessing…"
                >
                  Next
                </Button>
              </VStack>
            )}

            {isAssessing && <AnalysisChecklist />}
          </VStack>
        </Box>
      )}

      {/* Step: Results */}
      {step === 'results' && assessmentResult && (
        <PilotResults
          result={assessmentResult}
          partnerName={config.partnerName}
          isLandlord={occupancyType === 'landlord'}
          showLandlordEmail={occupancyType === 'tenant'}
          mortgageOrBankProvider={mortgageOrBankProvider}
          supportAvailable={config.supportAvailable ?? []}
          pricesUnlocked={pricesUnlocked}
          onGetPricesStart={handleGetPricesStart}
          onSubmitLead={handleLeadSubmit}
          isSubmittingLead={isSubmittingLead}
          leadError={leadError}
          onNewAddress={handleStartOver}
          onBack={() => setStep('address')}
        />
      )}
    </Box>
  )
}

export default PilotPage
